import { useFeatureAccess } from "../../../access-control/hooks/useFeatureAccess";
import { FEATURE_KEYS } from "../../../access-control/models/role-feature.models";
import { themeAtom } from "../../../shared/atoms";
import { ADMIN_CRON_GROUPS, ADMIN_CRON_JOBS, AdminCronJob } from "../../models/admin-cron.models";
import { adminStyles } from "../../styles/admin.styles";
import CronJobCard from "./CronJobCard";
import HoldCleaningDebugPanel from "./HoldCleaningDebugPanel";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const CronJobsNav = ({
  selectedJobId,
  onSelectJob,
  jobs,
}: {
  selectedJobId: string;
  onSelectJob: (jobId: string) => void;
  jobs: AdminCronJob[];
}) => {
  return (
    <aside data-test="admin-cron-secondary-nav" className={adminStyles.secondaryNav.shell}>
      <div className={adminStyles.secondaryNav.list}>
        {ADMIN_CRON_GROUPS.map((group) => {
          const groupJobs = jobs.filter((job) => job.group === group);

          if (groupJobs.length === 0) {
            return null;
          }

          return (
            <div key={group} className={adminStyles.secondaryNav.section}>
              <p className={adminStyles.secondaryNav.group}>{group}</p>
              <div className="space-y-1">
                {groupJobs.map((job) => {
                  const isActive = selectedJobId === job.id;

                  return (
                    <button
                      key={job.id}
                      type="button"
                      data-test={`admin-cron-nav-${job.id}`}
                      data-state={isActive ? "active" : "inactive"}
                      className={twMerge(
                        adminStyles.secondaryNav.item,
                        isActive ? adminStyles.secondaryNav.itemActive : adminStyles.secondaryNav.itemInactive,
                      )}
                      onClick={() => onSelectJob(job.id)}
                    >
                      {job.title}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};

/**
 * Admin cron launcher with a secondary navigation column and one focused action page.
 */
const CronJobsPanel = () => {
  const theme = useAtomValue(themeAtom);
  const { hasFeature } = useFeatureAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const jobs = useMemo(
    () => ADMIN_CRON_JOBS.filter((job) => hasFeature(job.requiredFeatureKey ?? FEATURE_KEYS.ADMIN_DEBUG_MONITORING)),
    [hasFeature],
  );
  const selectedJob = jobs.find((job) => job.id === searchParams.get("job")) ?? jobs[0];
  const selectJob = (jobId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("job", jobId);
    next.delete("retryRunId");
    setSearchParams(next);
  };

  if (!selectedJob) return null;

  return (
    <div data-test="admin-cron-jobs-panel" className="flex min-h-0 flex-1 overflow-hidden">
      <CronJobsNav selectedJobId={selectedJob.id} onSelectJob={selectJob} jobs={jobs} />

      <div className={twMerge("min-w-0 flex-1 overflow-hidden", theme.home.tailwind.textDefault)}>
        <div className={twMerge("flex h-full min-h-0 flex-col", adminStyles.page.pagePadding)}>
          <div className="mb-6">
            <p className={adminStyles.heading.eyebrow}>Debug</p>
            <h1 className={twMerge("mt-1", adminStyles.heading.pageTitle, theme.home.tailwind.textDefault)}>
              Cron jobs
            </h1>
            <p className={twMerge("mt-2", adminStyles.heading.body, theme.uwhcAssistant.textOutput)}>
              Manual controls for candidate checks and controlled production cron execution.
            </p>
          </div>

          {selectedJob.renderer === "holdCleaning" ? <HoldCleaningDebugPanel /> : <CronJobCard job={selectedJob} />}
        </div>
      </div>
    </div>
  );
};

export default CronJobsPanel;
